/**
 * Resume input signature — stable fingerprint of resume-impacting INPUT
 * fields only.  MUST stay byte-for-byte identical to the Python helper
 * at `backend/services/resume_signature.py`.
 *
 * Used by the Review step to detect staleness:
 *   currentInputSig = computeResumeInputSignature(resume)
 *   serverTruth     = resume.last_generated_input_hash    // set by server on regen
 *   isStale         = serverTruth && serverTruth !== currentInputSig
 *
 * localStorage is used **only** to remember per-browser dismissals, never
 * to determine freshness.
 *
 * STRICT RULES:
 *   - Inputs only. Never include AI-generated outputs (professional_summary
 *     / role ai_summary_text).
 *   - Never include timestamps, flags, derived caches, or IDs.
 *   - Deterministic at every level: canonical-JSON via sorted keys.
 *   - KSA arrays normalized to identifier strings, empties dropped,
 *     sorted (set-equality semantics).
 */

const DISMISS_KEY_PREFIX = 'st_resume_dismiss_';

// --- Field whitelists (must match backend) ----------------------------

const PERSONAL_FIELDS = [
  'full_name', 'street', 'city', 'state', 'zip_code', 'phone', 'email',
];
const MILITARY_ROLE_FIELDS = [
  'branch', 'rank',
  'mos_code', 'mos_title',
  'start_month', 'start_year', 'end_month', 'end_year',
  'location',
  'selected_knowledge', 'selected_skills', 'selected_abilities',
  'resume_bullets',
];
const CIVILIAN_FIELDS = [
  'employer', 'position',
  'start_month', 'start_year', 'end_month', 'end_year',
  'location', 'responsibilities',
];
const EDUCATION_FIELDS = [
  'school', 'degree', 'field',
  'start_month', 'start_year', 'end_month', 'end_year',
  'location',
];
const REFERENCE_FIELDS = ['name', 'relationship', 'contact'];

// --- Normalizers ------------------------------------------------------

const normString = (v) => (typeof v === 'string' ? v.trim() : (v ?? ''));

function normArrayItem(item) {
  if (item == null) return '';
  if (typeof item === 'string') return item.trim();
  if (typeof item === 'object') {
    for (const k of ['element_name', 'name', 'id']) {
      const v = item[k];
      if (v) return normString(v);
    }
    return canonicalStringify(item);
  }
  return String(item);
}

function pick(obj, fields) {
  if (!obj || typeof obj !== 'object') return {};
  const out = {};
  for (const k of [...fields].sort()) {
    let v = obj[k];
    if (Array.isArray(v)) {
      v = v.map(normArrayItem).filter((s) => s !== '').sort();
    } else {
      v = normString(v);
    }
    out[k] = v;
  }
  return out;
}

function normalizeArray(arr, fields) {
  if (!Array.isArray(arr)) return [];
  return arr.map((item) => pick(item, fields));
}

// Canonical JSON stringify — sorts keys at every level so output matches
// Python's `json.dumps(sort_keys=True, separators=(',', ':'))`.
function canonicalStringify(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return '[' + value.map(canonicalStringify).join(',') + ']';
  }
  const keys = Object.keys(value).sort();
  return '{' + keys.map((k) => JSON.stringify(k) + ':' + canonicalStringify(value[k])).join(',') + '}';
}

// --- Public API -------------------------------------------------------

export function computeResumeInputSignature(resume) {
  if (!resume || typeof resume !== 'object') return '';
  const normalized = {
    personal_info: pick(resume.personal_info, PERSONAL_FIELDS),
    military_roles: normalizeArray(resume.military_roles, MILITARY_ROLE_FIELDS),
    civilian_experience: normalizeArray(resume.civilian_experience, CIVILIAN_FIELDS),
    education: normalizeArray(resume.education, EDUCATION_FIELDS),
    awards: normString(resume.awards),
    references: normalizeArray(resume.references, REFERENCE_FIELDS),
  };
  return canonicalStringify(normalized);
}

// --- Per-browser dismissal (warning suppression only — NEVER freshness) ---
//
// When the user clicks "Keep Current", we remember the exact stale input
// hash they dismissed FOR THIS BROWSER ONLY. Returning to Review with the
// same currentInputSig will suppress the banner. Any further edit changes
// currentInputSig → the dismissal no longer matches → the banner returns.
// This dismissal is NEVER sent to the server and NEVER taken as proof of
// freshness.

function dismissKey(resumeId) {
  if (!resumeId) return null;
  return `${DISMISS_KEY_PREFIX}${resumeId}`;
}

export function getDismissedHash(resumeId) {
  const k = dismissKey(resumeId);
  if (!k) return null;
  try {
    const raw = window.localStorage.getItem(k);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.hash === 'string' ? parsed.hash : null;
  } catch {
    return null;
  }
}

export function setDismissedHash(resumeId, hash) {
  const k = dismissKey(resumeId);
  if (!k || typeof hash !== 'string') return;
  try {
    window.localStorage.setItem(k, JSON.stringify({ hash, dismissed_at: Date.now() }));
  } catch {
    /* fail-soft */
  }
}

export function clearDismissedHash(resumeId) {
  const k = dismissKey(resumeId);
  if (!k) return;
  try {
    window.localStorage.removeItem(k);
  } catch {
    /* fail-soft */
  }
}
